import React, { createContext, useContext, useMemo, useState } from 'react';

// Lightweight uuid v4 generator with crypto fallback.
function uuidv4(): string {
  if (typeof (global as any).crypto?.getRandomValues === 'function') {
    const buf = new Uint8Array(16);
    (global as any).crypto.getRandomValues(buf);
    // Per RFC4122 v4
    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;
    const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.substr(0,8)}-${hex.substr(8,4)}-${hex.substr(12,4)}-${hex.substr(16,4)}-${hex.substr(20,12)}`;
  }
  // fallback to Math.random-based UUID (not cryptographically secure)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

type SessionMap = Record<number, string>; // competitionId -> sessionId

type SessionContextType = {
  getSessionId: (competitionId: number) => string;
  resetSessionId: (competitionId: number) => void;
};

const SessionContext = createContext<SessionContextType | null>(null);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<SessionMap>({});

  const getSessionId = (competitionId: number) => {
    if (sessions[competitionId]) return sessions[competitionId];
    const id = uuidv4();
    setSessions(s => ({ ...s, [competitionId]: id }));
    return id;
  };

  const resetSessionId = (competitionId: number) => {
    setSessions(s => {
      const copy = { ...s };
      delete copy[competitionId];
      return copy;
    });
  };

  const value = useMemo(() => ({ getSessionId, resetSessionId }), [sessions]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = () => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
};
